import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faCircleNotch, faClock, faTimes } from '@fortawesome/free-solid-svg-icons';
import { backendUrl } from '../utils/variables';
import { timeAgo } from '../utils/functions';
import './TherapyChat.css';

// Helper function to safely parse dates for Safari compatibility
const parseDate = (dateString) => {
  if (!dateString) return null;
  
  // If it's already a Date object, return it
  if (dateString instanceof Date) return dateString;
  
  // Manual parsing for "YYYY-MM-DD HH:MM:SS" format
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    // Create date as local time (not UTC)
    const date = new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1, // Month is 0-indexed
      parseInt(day, 10),
      parseInt(hour, 10),
      parseInt(minute, 10),
      parseInt(second, 10)
    );
    
   
    
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Fallback to regular Date parsing
  try {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    console.error('Error parsing date:', dateString, error);
    return null;
  }
};

// Function to generate consistent masked name
const generateMaskedName = (userId, isTherapist = false) => {
  // Simple hash function to generate consistent number
  const hash = userId.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  
  // Convert hash to positive number and take last 3 digits
  const maskedNumber = Math.abs(hash).toString().slice(-3).padStart(3, '0');
  
  // Return masked name based on role
  return isTherapist ? `Therapist ${maskedNumber}` : `Patient ${maskedNumber}`;
};

// Generate a consistent color based on user ID
const generateAvatarColor = (userId) => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD',
    '#D4A5A5', '#9B59B6', '#3498DB', '#E67E22', '#2ECC71'
  ];
  const hash = userId.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  return colors[Math.abs(hash) % colors.length];
};



// Fixed SessionCountdown component with Safari-compatible date parsing
const SessionCountdown = ({ session, isTherapist, userId, onCloseSession }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isEndingSoon, setIsEndingSoon] = useState(false);

  useEffect(() => {
    // Use scheduled_time as the start time for both therapist and patient views
    const startTime = session.scheduled_time;
    const durationMinutes = parseInt(session.session_duration) || 60;

  /*   console.log('Session countdown debug:', {
      startTime: startTime,  
      durationMinutes: durationMinutes,
      isTherapist: isTherapist, 
      userId: userId,
      sessionId: session.session_id
    });  */      
    
    if (!startTime) {
      console.warn('No scheduled_time provided:', session);
      return;
    }

    const calculateTimeLeft = () => {
      const now = new Date();
      const startDate = parseDate(startTime);
      
      // If we couldn't parse the start date, don't proceed
      if (!startDate) {
        console.error('Could not parse start time:', startTime);
        setTimeLeft(null);
        setIsExpired(false);
        setIsEndingSoon(false);
        return;
      }
      
      const endTime = new Date(startDate.getTime() + (durationMinutes * 60 * 1000));
      
      // Debug logging for Safari
      /* console.log('Date parsing debug:', {
        original: startTime,
        parsed: startDate,
        now: now,
        endTime: endTime,
        isStartDateValid: !isNaN(startDate.getTime()),
        isEndTimeValid: !isNaN(endTime.getTime())
      }); */
      
      // If scheduled time is in the future, show time until session starts
      if (startDate > now) {
        const timeUntilStart = startDate - now;
        
        // Ensure we have a valid time difference
        if (isNaN(timeUntilStart) || timeUntilStart < 0) {
          console.error('Invalid time until start:', timeUntilStart);
          return;
        }
        
        const hours = Math.floor(timeUntilStart / (1000 * 60 * 60));
        const minutes = Math.floor((timeUntilStart % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeUntilStart % (1000 * 60)) / 1000);
        
        setTimeLeft({ hours, minutes, seconds, isWaiting: true });
        setIsExpired(false);
        setIsEndingSoon(false);
        return;
      }
      
      // Session has started, calculate time remaining
      const difference = endTime - now;
      
      // Ensure we have a valid time difference
      if (isNaN(difference)) {
        console.error('Invalid time difference:', difference);
        return;
      }
      
      if (difference <= 0) {
        setIsExpired(true);
        setIsEndingSoon(false);
        setTimeLeft(null);
        return;
      }
      
      // Check if session is ending soon (less than 10 minutes)
      if (difference < 10 * 60 * 1000) {
        setIsEndingSoon(true);
      } else {
        setIsEndingSoon(false);
      }
      
      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      // Ensure all values are numbers before setting state
      if (!isNaN(hours) && !isNaN(minutes) && !isNaN(seconds)) {
        setTimeLeft({ hours, minutes, seconds, isWaiting: false });
      } else {
        console.error('Invalid time calculations:', { hours, minutes, seconds });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [session.scheduled_time, session.session_duration]);

  // Use scheduled_time as the start time for both therapist and patient views
  const startTime = session.scheduled_time;
  const durationMinutes = parseInt(session.session_duration) || 60;
  
  if (!startTime) {
    return null;
  }

  if (isExpired) {
    return (
      <div className="d-flex align-items-center">
        <Badge bg="danger" className="me-2">
          <FontAwesomeIcon icon={faClock} className="me-1" />
          Session Expired
        </Badge>
        {isTherapist && session.status !== 'closed' && (
          <Button 
            size="sm" 
            variant="outline-danger"
            onClick={onCloseSession}
          >
            <FontAwesomeIcon icon={faTimes} className="me-1" />
            Close Session
          </Button>
        )}
      </div>
    );
  }

  if (!timeLeft) {
    return null;
  }

  // Enhanced formatTime function with NaN checking
  const formatTime = (time) => {
    if (isNaN(time) || time < 0) {
      console.warn('Invalid time value:', time);
      return '00';
    }
    return time.toString().padStart(2, '0');
  };

  // Show waiting message if session hasn't started yet
  if (timeLeft.isWaiting) {
    return (
      <Badge bg="secondary" className="ms-2">
        <FontAwesomeIcon icon={faClock} className="me-1" />
        Starts in {formatTime(timeLeft.hours)}:{formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)}
      </Badge>
    );
  }

  return (
    <Badge bg={isEndingSoon ? "warning" : "info"} className="ms-2">
      <FontAwesomeIcon icon={faClock} className="me-1" />
      {formatTime(timeLeft.hours)}:{formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)}
    </Badge>
  );
};

const TherapyChat = ({ 
  session, 
  userId, 
  onClose, 
  isTherapist,
  refreshSessions
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);

  const handleCloseSession = async () => {
    try {
      const response = await fetch(`${backendUrl}/update_therapy_session.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: session.session_id,
          status: 'closed',
          user_id: userId
        })
      });
      
      const data = await response.json();
      //console.log('Close session response:', data);
      //return;
      if (data.success) {
        // Update the session status locally
        session.status = 'closed';
        // Force re-render by updating messages state
        setMessages([...messages]);
        // Call parent refresh function if available
        if (refreshSessions) {
          refreshSessions();
        }
      } else {
        console.error('Failed to close session:', data.message);
      }
    } catch (err) {
      console.error('Error closing session:', err);
    }
  };

  // Fetch messages on component mount
  useEffect(() => {
    fetchMessages();
    
    // Set up polling for new messages
    pollingRef.current = setInterval(() => {
      fetchMessages(false); // Don't show loading state during polling
    }, 5000);
    
    // Clean up interval on unmount
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [session.session_id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }
  };

  const fetchMessages = async (showLoading = true) => {
    if (!session.session_id || !userId) return;
    
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      
      const response = await fetch(`${backendUrl}/fetch_therapy_messages.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          session_id: session.session_id
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Add masked names to messages, but use therapist's real name for patients
        const messagesWithMaskedNames = data.messages.map(message => {
          let displayName;
          
          // For patients, show therapist's real name if available
          if (!isTherapist && message.sender_type === 'therapist' && session.therapist_name) {
            displayName = session.therapist_name;
          } else {
            // Use masked name for privacy
            displayName = generateMaskedName(message.sender_id, message.sender_type === 'therapist');
          }
          
          return {
            ...message,
            masked_name: displayName,
            avatar_color: generateAvatarColor(message.sender_id)
          };
        });
        setMessages(messagesWithMaskedNames);
      } else {
        setError(data.message || 'Failed to load messages');
      }
    } catch (err) {
      setError('Network error. Please try again later.');
      console.error('Error fetching messages:', err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!newMessage.trim() || sending) return;
    
    try {
      setSending(true);
      
      const response = await fetch(`${backendUrl}/send_therapy_message.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sender_id: userId,
          session_id: session.session_id,
          sender_type: isTherapist ? 'therapist' : 'user',
          message: newMessage
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Clear the input first
        setNewMessage('');
        
        // Refresh messages to get the latest including the new message
        fetchMessages(false);
        
        // Don't automatically refresh sessions to avoid page reload issues
        // The parent component can handle session updates when needed
      } else {
        setError(data.message || 'Failed to send message');
      }
    } catch (err) {
      setError('Network error. Please try again later.');
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const formatMessages = () => {
    // Group consecutive messages from the same sender
    const groupedMessages = [];
    let currentGroup = null;
    
    messages.forEach(message => {
      if (!currentGroup || currentGroup.sender_id !== message.sender_id) {
        // Start a new group
        currentGroup = {
          sender_id: message.sender_id,
          masked_name: message.masked_name,
          avatar_color: message.avatar_color,
          sender_type: message.sender_type,
          messages: [message],
          timestamp: message.created_at
        };
        groupedMessages.push(currentGroup);
      } else {
        // Add to current group
        currentGroup.messages.push(message);
        // Update timestamp to the latest message
        currentGroup.timestamp = message.created_at;
      }
    });
    
    return groupedMessages;
  };
  
  // Helper to determine if the message group is from the current user
  const isCurrentUser = (senderId, senderType) => {
    // First use the sender_id to check if it matches the current user's ID
    if (senderId === userId) return true;
    
    // If that fails, use the sender_type and user role to determine if it's the current user
    // This is important because the sender_id may be different from user_id if it's a therapist
    if (senderType === 'therapist' && isTherapist) return true;
    if (senderType === 'user' && !isTherapist) return true;
    
    return false;
  };

  return (
    <div className="therapy-chat">
      <div className="therapy-chat-header">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h5 className='d-none'>
              {isTherapist ? 
                `Chat with ${generateMaskedName(session.user_id, false)}` : 
                `Chat with ${session.therapist_name || generateMaskedName(session.therapist_id, true)}`}
            </h5>
            <p className="text-muted mb-0">
              {session.status === 'active' ? 
                'Session active' : 
                `Session status: ${session.status}`}
            </p>
            {session.scheduled_time && (
              <div className="session-info">
                Scheduled: {parseDate(session.scheduled_time)?.toLocaleString() || 'Invalid date'} • {Math.round((session.session_duration || 60) / 60)} hour{(session.session_duration || 60) / 60 !== 1 ? 's' : ''}
              </div>
            )}
          </div>
          {/* Show countdown for both therapists and patients */}
          <SessionCountdown session={session} isTherapist={isTherapist} userId={userId} onCloseSession={handleCloseSession} />
        </div>
      </div>
      
      <div className="therapy-chat-body">
        {error && (
          <Alert variant="danger" className="m-2">
            {error}
          </Alert>
        )}
        
        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center p-4">
            <p className="text-muted">
              No messages yet. Send a message to start the conversation.
            </p>
          </div>
        ) : (
          <div className="messages-container">
            {formatMessages().map((group, idx) => (
              <div 
                key={idx} 
                className={`message-group ${isCurrentUser(group.sender_id, group.sender_type) ? 'user-message' : 'other-message'}`}
              >
                {!isCurrentUser(group.sender_id, group.sender_type) && (
                  <div className="sender-avatar">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ 
                        width: '40px', 
                        height: '40px',
                        backgroundColor: group.avatar_color,
                        color: '#fff',
                        fontSize: '1rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {group.masked_name.charAt(0)}
                    </div>
                  </div>
                )}
                
                <div className="message-content">
                  {!isCurrentUser(group.sender_id, group.sender_type) && (
                    <div className="sender-name">{group.masked_name}</div>
                  )}
                  
                  <div className="message-bubbles">
                    {group.messages.map(message => (
                      <div key={message.message_id} className="message-bubble">
                        {message.message_text}
                      </div>
                    ))}
                  </div>
                  
                  <div className="message-time">
                    {timeAgo(group.timestamp)}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>
      
      <div className="therapy-chat-footer">
        {session.status === 'active' ? (
          <Form onSubmit={handleSendMessage} onKeyDown={(e) => e.stopPropagation()}>
            <div className="d-flex">
              <Form.Control
                type="text"
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                disabled={sending}
              />
              <Button 
                type="submit" 
                variant="primary" 
                disabled={!newMessage.trim() || sending}
                className="ms-2"
                onClick={(e) => e.stopPropagation()}
              >
                {sending ? (
                  <FontAwesomeIcon icon={faCircleNotch} spin />
                ) : (
                  <FontAwesomeIcon icon={faPaperPlane} />
                )}
              </Button>
            </div>
          </Form>
        ) : (
          <p className="text-center text-muted mb-0">
            {session.status === 'pending' ? 
              "This session is pending approval. Chat will be available once activated." :
             session.status === 'hold' ? 
              "This session is on hold. Chat will be available once activated." :
             session.status === 'closed' ?
              "This session is closed. You can view the chat history but cannot send new messages." :
              "This session is not active. Chat will be available once activated."}
          </p>
        )}
      </div>
    </div>
  );
};

export default TherapyChat; 